import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

type Variant =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'pending'
    | 'danger'
    | 'dark'
    // Legacy/compat variants (mapped internally)
    | 'outline'
    | 'ghost'
    | 'destructive'
    | 'outline-primary'
    | 'outline-secondary'
    | 'outline-success'
    | 'outline-warning'
    | 'outline-pending'
    | 'outline-danger'
    | 'outline-dark'
    | 'soft-primary'
    | 'soft-secondary'
    | 'soft-success'
    | 'soft-warning'
    | 'soft-pending'
    | 'soft-danger'
    | 'soft-dark'
    | 'facebook'
    | 'twitter'
    | 'instagram'
    | 'linkedin';

type Elevated = boolean;
type Size = 'sm' | 'lg';
type Rounded = boolean;

type ButtonProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
    C,
    {
        as?: C extends string ? 'button' | 'a' : C;
        variant?: Variant;
        elevated?: Elevated;
        size?: Size;
        rounded?: Rounded;
    }
>;

type ButtonComponent = <C extends React.ElementType = 'button'>(props: ButtonProps<C>) => React.ReactElement | null | React.ReactNode;

const Button: ButtonComponent = forwardRef(<C extends React.ElementType>({ as, size, variant, elevated, rounded, children, ...props }: ButtonProps<C>, ref?: PolymorphicRef<C>) => {
    const Component = as || 'button';

    // General Styles
    // Hover, clique, foco e sombra ficam em assets/css/components/_botao.css (29/10):
    // .botao faz o fundo varrer da esquerda, subir 1px e a seta avançar; cada variante
    // escolhe o tom do varrido com .botao--principal, --contorno, --neutro ou --suave.
    const generalStyles = [
        'botao border inline-flex items-center justify-center py-2.5 px-4 rounded-none font-inter font-semibold text-sm cursor-pointer', // Default — manual de marca: botão reto, Inter 600, texto 14px
        '[&:not(button)]:text-center', // Not a button element
        'disabled:opacity-70 disabled:cursor-not-allowed', // Disabled
    ];

    // Sizes
    const small = ['text-[13px] py-2 px-3'];
    const large = ['text-base py-2.5 px-5'];

    // Main Colors
    // Roxo 700 (bg-botao) nos dois temas; no escuro o fio Roxo 400 da borda separa
    // o botão do fundo (pedido 29/10: o principal saía lilás).
    const primary = [
        'botao--principal bg-botao border-botao text-white', // Default
        'dark:border-roxo-400/80', // Dark mode
    ];
    // Rodada 2 (28/10): o cinza claro deixava o sistema "opaco". Secondary agora é
    // Neutro 200 com borda Neutro 300 e texto Neutro 800; no hover, toque roxo.
    const secondary = [
        'botao--neutro bg-neutro-200 border-neutro-300 text-neutro-800', // Default
        'dark:border-darkmode-300 dark:bg-darkmode-400 dark:text-slate-200', // Dark mode
    ];
    const success = [
        'bg-success border-success text-slate-900', // Default
        'dark:border-success', // Dark mode
    ];
    const warning = [
        'bg-warning border-warning text-slate-900', // Default
        'dark:border-warning', // Dark mode
    ];
    const pending = [
        'bg-pending border-pending text-white', // Default
        'dark:border-pending', // Dark mode
    ];
    const danger = [
        'bg-danger border-danger text-white', // Default
        'dark:border-danger', // Dark mode
    ];
    const dark = [
        'bg-dark border-dark text-white', // Default
        'dark:bg-darkmode-300 dark:border-darkmode-200 dark:text-slate-100', // Dark mode
    ];

    // Social Media
    const facebook = ['bg-[#3b5998] border-[#3b5998] text-white dark:border-[#3b5998]'];
    const twitter = ['bg-[#4ab3f4] border-[#4ab3f4] text-white dark:border-[#4ab3f4]'];
    const instagram = ['bg-[#517fa4] border-[#517fa4] text-white dark:border-[#517fa4]'];
    const linkedin = ['bg-[#0077b5] border-[#0077b5] text-white dark:border-[#0077b5]'];

    // Outline
    // Contorno roxo: no hover o Roxo 700 enche da esquerda e o texto fica branco.
    const outlinePrimary = [
        'botao--contorno border-botao text-botao', // Default
        'dark:border-roxo-400/80 dark:text-roxo-300', // Dark mode
    ];
    // Contorno neutro com fundo Neutro 100, borda Neutro 400 e texto Neutro 800.
    const outlineSecondary = [
        'botao--neutro bg-neutro-100 border-neutro-400 text-neutro-800', // Default
        'dark:bg-darkmode-600 dark:border-darkmode-200/60 dark:text-slate-200', // Dark mode
    ];
    const outlineSuccess = [
        'botao--suave border-success text-success', // Default
        'dark:border-success', // Dark mode
    ];
    const outlineWarning = [
        'botao--suave border-warning text-warning', // Default
        'dark:border-warning', // Dark mode
    ];
    const outlinePending = [
        'botao--suave border-pending text-pending', // Default
        'dark:border-pending', // Dark mode
    ];
    const outlineDanger = [
        'botao--suave border-danger text-danger', // Default
        'dark:border-danger', // Dark mode
    ];
    const outlineDark = [
        'botao--suave border-dark text-dark', // Default
        'dark:border-darkmode-100/50 dark:text-slate-200', // Dark mode
    ];

    // Soft Color
    const softPrimary = [
        'botao--suave bg-primary border-primary bg-opacity-20 border-opacity-5 text-primary', // Default
        'dark:border-opacity-100 dark:bg-opacity-20 dark:border-primary', // Dark mode
    ];
    const softSecondary = [
        'botao--neutro bg-neutro-200/70 border-neutro-300 text-neutro-800', // Default
        'dark:bg-darkmode-400/60 dark:border-darkmode-300 dark:text-slate-200', // Dark mode
    ];
    const softSuccess = [
        'botao--suave bg-success border-success bg-opacity-20 border-opacity-5 text-success', // Default
        'dark:border-success dark:border-opacity-20', // Dark mode
    ];
    const softWarning = [
        'botao--suave bg-warning border-warning bg-opacity-20 border-opacity-5 text-warning', // Default
        'dark:border-warning dark:border-opacity-20', // Dark mode
    ];
    const softPending = [
        'botao--suave bg-pending border-pending bg-opacity-20 border-opacity-5 text-pending', // Default
        'dark:border-pending dark:border-opacity-20', // Dark mode
    ];
    const softDanger = [
        'botao--suave bg-danger border-danger bg-opacity-20 border-opacity-5 text-danger', // Default
        'dark:border-danger dark:border-opacity-20', // Dark mode
    ];
    const softDark = [
        'botao--suave bg-dark border-dark bg-opacity-20 border-opacity-5 text-dark', // Default
        'dark:bg-darkmode-800/30 dark:border-darkmode-800/60 dark:text-slate-300', // Dark mode
    ];

    return (
        <Component
            {...props}
            ref={ref}
            className={twMerge([
                generalStyles,
                size == 'sm' && small,
                size == 'lg' && large,
                variant == 'primary' && primary,
                variant == 'secondary' && secondary,
                variant == 'success' && success,
                variant == 'warning' && warning,
                variant == 'pending' && pending,
                variant == 'danger' && danger,
                variant == 'dark' && dark,
                // Legacy/compat variants mapping
                variant == 'outline' && outlineSecondary,
                variant == 'ghost' && outlineSecondary,
                variant == 'destructive' && danger,
                variant == 'outline-primary' && outlinePrimary,
                variant == 'outline-secondary' && outlineSecondary,
                variant == 'outline-success' && outlineSuccess,
                variant == 'outline-warning' && outlineWarning,
                variant == 'outline-pending' && outlinePending,
                variant == 'outline-danger' && outlineDanger,
                variant == 'outline-dark' && outlineDark,
                variant == 'soft-primary' && softPrimary,
                variant == 'soft-secondary' && softSecondary,
                variant == 'soft-success' && softSuccess,
                variant == 'soft-warning' && softWarning,
                variant == 'soft-pending' && softPending,
                variant == 'soft-danger' && softDanger,
                variant == 'soft-dark' && softDark,
                variant == 'facebook' && facebook,
                variant == 'twitter' && twitter,
                variant == 'instagram' && instagram,
                variant == 'linkedin' && linkedin,
                rounded && 'rounded-full',
                elevated && 'shadow-md',
                props.className,
            ])}
        >
            {children}
        </Component>
    );
});

export default Button;
